import * as React from 'react';
import { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { R } from '@/features/profile/ProfileResources.resx';
import { IKid, ISelfResponse } from '@/shared/interfaces';
import { Button } from '@/components/Button';
import { useSetAtom } from 'jotai';
import { kidsAtom, profileAtom } from '@/features/profile/MinorChildren/store';
import editIcon from '../../../../wwwroot/images/icons/edit.svg';
import trashIcon from '../../../../wwwroot/images/icons/trash.svg';
import { ChildModal } from '@/features/profile/MinorChildren/ChildModal/ChildModal';
import { useHydrateAtoms } from 'jotai/utils';

import { useAtom } from 'jotai/index';
import { useProfile } from '@/features/profile/MinorChildren/hooks/useProfile';
import { ConfirmDeletionModal } from '@/features/profile/MinorChildren/ConfirmDeletionModal/ConfirmDeletionModal';
import { toast } from '@/shared/Toast';

export function MinorChildrenApp(
    containerQuery: string,
    selfResponse: ISelfResponse,
    childTerms: string,
) {
    const elm = document.querySelector(containerQuery);
    const root = createRoot(elm);
    root.render(
        <MinorChildren selfResponse={selfResponse} childTerms={childTerms} />,
    );
}

type MinorChildrenProps = {
    selfResponse: ISelfResponse;
    childTerms: string;
};
const MinorChildren: ({
    selfResponse,
    childTerms,
}: MinorChildrenProps) => React.ReactNode = ({ selfResponse, childTerms }) => {
    const setProfileAtom = useSetAtom(profileAtom);
    useHydrateAtoms([[profileAtom, selfResponse]]);

    const [kids] = useAtom(kidsAtom); // use this derived atom
    const { refetch } = useProfile(); // refetch data when updating, adding or deleting a child.

    // Refetch and populate the profile atom again
    const handleRefetch = async () => {
        const updatedProfile = await refetch();

        if (updatedProfile?.error) {
            toast.addToast({
                type: 'error',
                message: R.RefreshDataError,
                autoClose: true,
            });
            return;
        }

        if (updatedProfile?.data) {
            setProfileAtom(updatedProfile.data);
        }
    };

    const [deleteModalForKid, setDeleteModalForKid] = useState<IKid>(null);
    const [editModalForKid, setEditModalForKid] = useState<IKid>(null);
    const [addModalForKid, setAddModalForKid] = useState<boolean>(false);

    const openDeleteKidModal = (kid: IKid): void => {
        setDeleteModalForKid(kid);
    };
    const closeDeleteKidModal = (): void => {
        setDeleteModalForKid(null);
    };

    const openEditKidModal = (kid: IKid): void => {
        setEditModalForKid(kid);
    };
    const closeEditKidModal = (): void => {
        setEditModalForKid(null);
    };

    const openAddKidModal = (): void => {
        setAddModalForKid(true);
    };
    const closeAddKidModal = (): void => {
        setAddModalForKid(false);
    };

    return (
        <>
            <p className="max-w-prose" id="children-profile-description">
                {R.Profile_Description_Children}
            </p>
            {kids?.map((kid) => (
                <div
                    className="my-5 flex flex-col gap-2"
                    aria-describedby="children-profile-description"
                    key={kid.UID}
                >
                    <div className="flex flex-row items-center gap-4">
                        <Button
                            variant="secondary"
                            outline={true}
                            className="btn-input-like-stroke flex w-full max-w-sm items-center justify-between rounded-2xl p-4 text-left"
                            aria-labelledby="child-name edit-action"
                            onClick={() => openEditKidModal(kid)}
                        >
                            <div className="flex-1">
                                <div
                                    id="child-name"
                                    className="text-lg font-bold leading-tight text-grey-900"
                                >
                                    {kid.Name}
                                </div>
                                <div className="text-sm font-normal leading-tight text-grey-700">
                                    {kid.FormattedCpr}
                                </div>
                            </div>
                            <img
                                id="edit-action"
                                className="flex-none"
                                width="24"
                                height="24"
                                src={editIcon}
                                alt={R.OpenEditChildModalA11y}
                            />
                        </Button>
                        <button
                            onClick={() => openDeleteKidModal(kid)}
                            aria-labelledby="child-name delete-action"
                        >
                            <img
                                id="delete-action"
                                className="block cursor-pointer"
                                width="24"
                                height="24"
                                src={trashIcon}
                                alt={R.OpenDeleteChildModalA11y}
                            />
                        </button>
                    </div>
                </div>
            ))}

            <button className="btn btn-primary" onClick={openAddKidModal}>
                {R.Profile_AddChild}
            </button>

            {deleteModalForKid && (
                <ConfirmDeletionModal
                    kid={deleteModalForKid}
                    title={R.RemoveChild}
                    onClose={closeDeleteKidModal}
                    onMutationSuccess={handleRefetch}
                />
            )}

            {editModalForKid && (
                <ChildModal
                    kid={editModalForKid}
                    onClose={closeEditKidModal}
                    onMutationSuccess={handleRefetch}
                    mode={{ isEditing: true, isAdding: false }}
                    childTerms={childTerms}
                />
            )}

            {addModalForKid && (
                <ChildModal
                    onClose={closeAddKidModal}
                    onMutationSuccess={handleRefetch}
                    mode={{ isEditing: false, isAdding: true }}
                    childTerms={childTerms}
                />
            )}
        </>
    );
};
